// Radha — ECL provision matrix (081). CFO track #3.
//
// The quarterly argument with the auditors, laid out as the argument actually
// goes: exposure per ageing bucket × a loss rate, every rate showing WHOSE
// number it is (tenant policy vs illustrative default), plus a forward-looking
// overlay collected as an explicit management judgment — never smuggled into
// a rate. The panel says plainly that it is a working paper, not accounting
// advice.
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { useRole } from "../lib/authSupabase";
import { fmtMoney, fmtMoneyFull, fmtDate } from "../lib/money";
import { ExportButton } from "./TableSearch";

interface EclRow {
  bucket: string; bucket_label: string; dpd_from: number; dpd_to: number;
  exposure: string; invoice_count: number;
  rate_pct: string; rate_source: string; rate_set_at: string | null;
  provision: string;
}

function useEclMatrix() {
  return useQuery({
    queryKey: ["ecl-matrix"],
    queryFn: async (): Promise<EclRow[]> => {
      const { data, error } = await supabase.rpc("ecl_provision_matrix");
      if (error) throw error;
      return (data as EclRow[]) ?? [];
    },
    staleTime: 60_000,
  });
}

function useSetRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ bucket, rate }: { bucket: string; rate: number }) => {
      // tenant row may not exist yet (defaults live on tenant_id NULL): try
      // update-own first, insert when nothing matched.
      const upd = await supabase.from("ecl_loss_rates")
        .update({ rate_pct: rate, note: "Set in app" })
        .eq("bucket", bucket).not("tenant_id", "is", null).select("id");
      if (upd.error) throw upd.error;
      if ((upd.data ?? []).length === 0) {
        const ins = await supabase.from("ecl_loss_rates")
          .insert({ bucket, rate_pct: rate, note: "Set in app" });
        if (ins.error) throw ins.error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ecl-matrix"] }),
  });
}

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "8px" };
const num: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };

export function EclPanel() {
  const { data, isLoading } = useEclMatrix();
  const { canWrite } = useRole();
  const setRate = useSetRate();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [overlayPct, setOverlayPct] = useState(0);

  if (isLoading || !data || data.length === 0) return null;

  const exposure = data.reduce((s, r) => s + Number(r.exposure), 0);
  const baseEcl = data.reduce((s, r) => s + Number(r.provision), 0);
  const overlay = baseEcl * (overlayPct / 100);
  const totalEcl = baseEcl + overlay;
  const anyDefault = data.some((r) => r.rate_source !== "tenant policy");

  const commit = (bucket: string) => {
    const n = Number(draft);
    if (!(n >= 0 && n <= 100)) { setEditing(null); return; }
    setRate.mutate({ bucket, rate: n });
    setEditing(null);
  };

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", marginBottom: 22 }}>
      <div style={{ font: "600 15px Inter", marginBottom: 4 }}>Expected credit loss — provision matrix</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 12px", maxWidth: 780 }}>
        Simplified approach under IFRS 9 / Ind AS 109 (B5.5.35): lifetime ECL as ageing buckets × loss rates.
        A working paper for the provision discussion — <strong>not accounting advice</strong>; rates are policy
        inputs your auditors will ask you to defend from your own loss history.
      </p>

      <div style={{ display: "flex", gap: 24, margin: "0 0 14px", flexWrap: "wrap" }}>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Gross receivables</div>
          <div style={{ font: "600 20px Inter" }}>{fmtMoney(exposure)}</div></div>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Matrix ECL</div>
          <div style={{ font: "600 20px Inter", color: "var(--status-warning)" }}>{fmtMoney(baseEcl)}</div></div>
        <div title="A forward-looking adjustment (macro outlook, sector stress) applied on top of the matrix — IFRS 9 requires it to be an explicit, documented judgment. It is labelled as yours, not computed.">
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Management overlay</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <input type="number" value={overlayPct} min={-50} max={100}
              onChange={(e) => setOverlayPct(Number(e.target.value) || 0)}
              style={{ width: 58, padding: "3px 6px", fontSize: 14, border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--fg)" }} />
            <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>% → {fmtMoneyFull(overlay)}</span>
          </div></div>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Total provision</div>
          <div style={{ font: "600 20px Inter", color: "var(--status-critical)" }}>
            {fmtMoney(totalEcl)}
            <span style={{ fontSize: 12.5, fontWeight: 400, color: "var(--fg-muted)" }}> · {(totalEcl / exposure * 100).toFixed(1)}% coverage</span>
          </div></div>
      </div>

      {anyDefault && (
        <p style={{ fontSize: 12, color: "var(--status-warning)", margin: "0 0 10px" }}>
          ⚠ Some rates are illustrative defaults, not your policy. Click a rate to set your own — each change is
          stamped with who and when, which is exactly what the auditor asks for.
        </p>
      )}

      <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 11.5 }}>
            <th style={th}>Ageing bucket</th>
            <th style={{ ...th, textAlign: "right" }}>Exposure</th>
            <th style={{ ...th, textAlign: "right" }}>Invoices</th>
            <th style={{ ...th, textAlign: "right" }}>Loss rate</th>
            <th style={th}>Rate source</th>
            <th style={{ ...th, textAlign: "right" }}>ECL</th>
          </tr></thead>
          <tbody>
            {data.map((r) => (
              <tr key={r.bucket} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={td}>{r.bucket_label}</td>
                <td style={num}>{fmtMoney(r.exposure)}</td>
                <td style={num}>{r.invoice_count}</td>
                <td style={{ ...num, cursor: canWrite ? "pointer" : "default" }}
                  onClick={() => { if (canWrite && editing !== r.bucket) { setEditing(r.bucket); setDraft(String(Number(r.rate_pct))); } }}>
                  {editing === r.bucket ? (
                    <input autoFocus type="number" value={draft} min={0} max={100} step={0.5}
                      onChange={(e) => setDraft(e.target.value)}
                      onBlur={() => commit(r.bucket)}
                      onKeyDown={(e) => { if (e.key === "Enter") commit(r.bucket); if (e.key === "Escape") setEditing(null); }}
                      style={{ width: 64, padding: "2px 6px", fontSize: 13, border: "1px solid var(--accent-purple)", borderRadius: 5, background: "var(--surface)", color: "var(--fg)", textAlign: "right" }} />
                  ) : (
                    <span style={{ fontWeight: 600, borderBottom: canWrite ? "1px dashed var(--border-strong)" : "none" }}>
                      {Number(r.rate_pct)}%
                    </span>
                  )}
                </td>
                <td style={{ ...td, fontSize: 12, color: r.rate_source === "tenant policy" ? "var(--status-good)" : "var(--status-warning)" }}>
                  {r.rate_source}
                  {r.rate_set_at && <span style={{ color: "var(--fg-faint)" }}> · {fmtDate(r.rate_set_at)}</span>}
                </td>
                <td style={{ ...num, fontWeight: 600 }}>{fmtMoneyFull(r.provision)}</td>
              </tr>
            ))}
            <tr style={{ borderTop: "2px solid var(--border-strong)" }}>
              <td style={{ ...td, fontWeight: 600 }}>Total</td>
              <td style={{ ...num, fontWeight: 600 }}>{fmtMoney(exposure)}</td>
              <td style={num}>{data.reduce((s, r) => s + Number(r.invoice_count), 0)}</td>
              <td style={num} />
              <td style={td} />
              <td style={{ ...num, fontWeight: 700 }}>{fmtMoneyFull(baseEcl)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", alignItems: "center", marginTop: 10 }}>
        <p style={{ color: "var(--fg-faint)", fontSize: 11, margin: 0, maxWidth: 640 }}>
          Exposure at carrying rates; DPD measured against due date today. The overlay is applied to the matrix
          total and exported with the working so the judgment stays visible.
        </p>
        <span style={{ marginInlineStart: "auto" }}>
          <ExportButton name="ECL provision working" rows={() => [
            ...data.map((r) => ({
              Bucket: r.bucket_label, Exposure: Number(r.exposure), Invoices: r.invoice_count,
              "Rate %": Number(r.rate_pct), "Rate source": r.rate_source, ECL: Number(r.provision),
            })),
            { Bucket: "Matrix total", Exposure: exposure, Invoices: "", "Rate %": "", "Rate source": "", ECL: baseEcl },
            { Bucket: `Management overlay (${overlayPct}%)`, Exposure: "", Invoices: "", "Rate %": "", "Rate source": "management judgment", ECL: overlay },
            { Bucket: "Total provision", Exposure: "", Invoices: "", "Rate %": "", "Rate source": "", ECL: totalEcl },
          ]} />
        </span>
      </div>
    </div>
  );
}
